import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Check, X as XIcon, Spinner } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { toEnglishDigits } from "@/utils/toEnglishDigits";

/**
 * InlineCell — click any table cell to edit its value in-place.
 *
 * Behaviour:
 *  - Click     → switches to input mode (focused + selected).
 *  - Enter/Blur → saves the change (skipped if unchanged).
 *  - Escape    → cancels and reverts.
 *  - Anywhere → e.stopPropagation() so the row's onClick (open booking
 *    details modal) never fires while editing.
 *
 * Save flow:
 *  - `endpoint` + `method` (default: `PATCH /admin/bookings/{id}` with
 *    { [field]: value }). Callers can override for special routes such as
 *    the wage endpoint (`PUT /admin/bookings/{id}/total-wage`).
 *  - Validation errors from the backend surface as toasts; the cell reverts
 *    to display mode so the admin isn't locked into a broken value.
 *
 * Props:
 *   value      — displayed value when not editing.
 *   type       — "text" | "number" | "date" | "time" (default: text).
 *   field      — payload key for a plain PATCH ({field: value}). Ignored
 *                if `buildPayload` is provided.
 *   endpoint   — full path, defaults to `/admin/bookings/{bookingId}`.
 *   method     — "patch" (default) | "put".
 *   buildPayload(newVal) — optional custom payload builder.
 *   display    — optional function returning the display JSX (for colored
 *                numbers, formatting, etc.).
 *   placeholder — shown when value is empty.
 *   inputProps — extra props for the underlying <input>.
 *   onSaved    — callback with (updatedBooking, newVal) after successful save.
 *   testId
 */
export default function InlineCell({
  bookingId,
  value,
  displayValue,
  type = "text",
  field,
  endpoint,
  method = "patch",
  buildPayload,
  display,
  placeholder = "—",
  inputProps = {},
  onSaved,
  testId,
  align = "start",
  className = "",
  colorFor,
  cancelOnBlur = false,
  options,  // 2026-02-28 §PaymentMethod: when provided, renders a <select>
            // instead of an input. Array of {value, label} objects.
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      // No auto-select on focus — cursor is placed at end so the admin
      // keeps full control over what to add/remove ("كتابة يدوية").
      try {
        const el = inputRef.current;
        const len = el.value?.length ?? 0;
        if (typeof el.setSelectionRange === "function" && (type === "text")) {
          el.setSelectionRange(len, len);
        }
      } catch { /* number/date/time inputs don't support setSelectionRange */ }
    }
  }, [editing, type]);

  const start = (e) => {
    e?.stopPropagation();
    if (saving || editing) return;
    setDraft(value == null ? "" : String(value));
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setDraft("");
  };

  // 2026-02-28 §PaymentMethod — allow save with an explicit value (used by
  // <select> to save on change without waiting for onBlur). Falls back to
  // reading from `draft` if no override is given.
  const saveWithVal = async (overrideVal) => {
    const draftVal = overrideVal !== undefined ? overrideVal : draft;
    // Skip API call when unchanged (compare stringified values).
    const orig = value == null ? "" : String(value);
    if (String(draftVal) === orig) { cancel(); return; }

    // Type-aware payload coercion.
    let payloadVal = draftVal;
    if (type === "number") {
      const n = parseFloat(String(draftVal).replace(",", "."));
      payloadVal = Number.isFinite(n) ? n : 0;
    } else if (type === "time") {
      const s = String(draftVal).trim();
      if (s && !/^(\d{1,2}):(\d{2})$/.test(s)) {
        toast.error("صيغة الوقت غير صحيحة (HH:MM)");
        cancel();
        return;
      }
      payloadVal = s;
    } else {
      payloadVal = String(draftVal).trim();
    }

    setSaving(true);
    try {
      const url = endpoint || `/admin/bookings/${bookingId}`;
      const body = buildPayload ? buildPayload(payloadVal) : { [field]: payloadVal };
      const res = method === "put"
        ? await api.put(url, body)
        : await api.patch(url, body);
      toast.success("تم الحفظ");
      onSaved?.(res?.data?.booking || res?.data, payloadVal);
      setEditing(false);
    } catch (err) {
      toast.error(formatApiError(err));
      // Revert to display mode so the admin can retry without being stuck.
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };
  const save = () => saveWithVal();

  const onKey = (e) => {
    if (e.key === "Enter") { e.preventDefault(); save(); }
    else if (e.key === "Escape") { e.preventDefault(); cancel(); }
    e.stopPropagation();
  };

    if (editing) {
    const isNumeric = type === "number";
    return (
      <span
        className="inline-flex items-center gap-1"
        onClick={(e) => e.stopPropagation()}
      >
        {options && Array.isArray(options) ? (
          // 2026-02-28 §PaymentMethod — dropdown variant. Auto-saves on
          // change (unlike text inputs which save on Enter/blur).
          <select
            ref={inputRef}
            value={draft}
            onChange={(e) => {
              const v = e.target.value;
              setDraft(v);
              // Save immediately after picking a new option (no Enter needed).
              setTimeout(() => saveWithVal(v), 0);
            }}
            onKeyDown={(e) => { if (e.key === "Escape") { e.preventDefault(); cancel(); } }}
            onBlur={cancelOnBlur ? cancel : undefined}
            disabled={saving}
            data-testid={testId ? `${testId}-input` : undefined}
            className={`num bg-[#0F2547] border border-primary/60 rounded-md px-2 py-1 text-sm text-white min-w-[100px] focus:outline-none focus:ring-2 focus:ring-primary/40 ${className}`}
          >
            {options.map((o) => (
              <option key={String(o.value)} value={o.value}>{o.label}</option>
            ))}
          </select>
        ) : (
          <input
            ref={inputRef}
            // Numeric fields use inputMode + pattern instead of type="number" so
            // the browser hides the spinner arrows (manual typing only).
            type={isNumeric ? "text" : type}
            inputMode={isNumeric ? "decimal" : undefined}
            pattern={isNumeric ? "[0-9]*\\.?[0-9]*" : undefined}
            // step=60s (1 minute) — allow ANY minute (spec 2026-07 §7).
            step={type === "time" ? 60 : undefined}
            lang={type === "time" || type === "date" ? "en-GB" : undefined}
            value={draft}
            onChange={(e) => {
              const v = e.target.value;
              if (isNumeric) {
                // Allow digits, dot, and empty string only — no letters, no spinner side-effects.
                if (/^[0-9]*\.?[0-9]*$/.test(v) || v === "") setDraft(v);
                return;
              }
              setDraft(v);
            }}
            onKeyDown={onKey}
            onBlur={cancelOnBlur ? cancel : save}
            disabled={saving}
            data-testid={testId ? `${testId}-input` : undefined}
            dir={type === "text" ? "auto" : "ltr"}
            className={`num bg-[#0F2547] border border-primary/60 rounded-md px-2 py-1 text-sm text-white min-w-[80px] max-w-[200px] focus:outline-none focus:ring-2 focus:ring-primary/40 ${className}`}
            {...inputProps}
          />
        )}
        {saving && <Spinner size={12} className="animate-spin text-primary" />}
      </span>
    );
  }

  const shown = value == null || value === ""
    ? <span className="text-white/35">{placeholder}</span>
    : display
      ? display(value)
      : type === "number"
        ? <span className="num" style={colorFor ? { color: colorFor(Number(value)) } : undefined}>{toEnglishDigits(String(value))}</span>
        : <span dir={type === "text" ? "auto" : "ltr"}>{toEnglishDigits(String(displayValue ?? value))}</span>;

  return (
    <button
      type="button"
      onClick={start}
      data-testid={testId}
      title="انقر للتعديل"
      className={`inline-flex items-center text-${align} cursor-text hover:text-primary transition-colors ${className}`}
      style={{
        borderBottom: "1px dashed rgba(201,169,97,0.4)",
        paddingBottom: 1,
      }}
    >
      {shown}
    </button>
  );
}
